(ns insurance.views
  (:require
    [reagent.core :as r]
    [reagent.format :as fmt]
    [very.re-frame.core :as rf]
    [main.common :as common]
    [profile.common :as pc]
    [util.core :refer [string->float long-str to-local-time]]
    [css.core :refer [defstyled]]
    [cljss.core :as cljss]
    [css.vars :as vars]
    [css.typography :as typo]
    [css.form :as form]))

(defstyled policy-grid-s :li
  {:display "grid"
   :grid-template-columns "repeat(6, 1fr)"
   :grid-column-gap "30px"
   :border-bottom "1px solid #bdbdbd"
   :margin-top "30px"
   :padding-bottom "30px"
   ::cljss/media {[[:max-width "768px"]]
                  {:display "flex"
                   :flex-direction "column"}}})

(defstyled insurance-policy-row-s :div
  {:margin-bottom "1rem"
   :padding-bottom ".4rem"
   :border-bottom (str "1px solid " vars/border-light)})

(defonce tmp-count (atom 0))

(defn- nxt-tmp []
  (swap! tmp-count inc)
  (deref tmp-count))

(defn- empty-policy []
  {:id (random-uuid)
   :policy-type ""
   :carrier ""
   :amount ""
   :premium-amount ""
   :premium-due-date ""
   :issue-date ""
   :exp-date ""
   :beneficiaries ""
   :tmp? true
   :order (nxt-tmp)})

(defn empty-policy? [a]
  (every? empty? (map a [:policy-type :carrier :amount :premium-amount :premium-due-date :issue-date :exp-date :beneficiaries])))

(defn- hydrate-policies [policies]
  (if (empty? policies)
    [(empty-policy)]
    policies))

(defn manage-insurance [{:keys [on-save on-cancel cancel-label] :or {cancel-label "Cancel"}}]
  (let [as (rf/subscribe [:insurance])

        policy-form (atom {})

        tmp-policies (r/atom (hydrate-policies @as))
        add-policy (fn [] (swap! tmp-policies conj (empty-policy)))
        remove-policy (fn [a]
                       (swap! tmp-policies (fn [as]
                                            (remove #(= (:id a) (:id %)) as))))

        get-form-values (fn [x form]
                          (reduce (fn [m [k f]]
                                    (assoc m k
                                           (if (contains? #{:amount
                                                            :premium-amount} k)
                                             (string->float (.-value f))
                                             (.-value f))))
                                  {}
                                  (get form (:id x))))

        extract-policy (fn [policy policy-form]
                         (if (instance? cljs.core/UUID (:id policy))
                           (get-form-values policy policy-form)
                           (assoc (get-form-values policy policy-form) :id (:id policy))))

        gather (fn [e]
                 (.preventDefault e)
                 (let [policies (reduce (fn [c a] (conj c (extract-policy a @policy-form))) [] @tmp-policies)]
                   (on-save policies)))]
    (fn []
      [common/form-page-s
       [common/form-s {:on-submit gather}
        [common/form-header-s
         [common/form-title-s {:margin "45px 0 0"} "Insurance"]
         [common/form-explain-s "Insurance is a form of risk management that helps protect your assets in the event of unforeseen circumstances. Having a record of your insurance policies will help identify any gaps in your overall risk management plan."]]
        [pc/form-section-s
         [pc/form-section-header-s
          [pc/form-section-title-s "Insurance"]]
         [pc/form-section-description-s "Please enter policy information for everyone in your household."]
         ;; this should be in its own fn but then we lose access to the form
         ;; inputs and stateless-ness
         [:div
          [:ul
           (doall (for [a (sort-by #(when (contains? % :tmp?)
                                      (:order %)) @tmp-policies)]
                    ^{:key (str (:id a))}
                    [policy-grid-s
                     [form/input-row-s {:grid-column "span 3"}
                      [form/input-label-s "Type of insurance"]
                      [form/select {:default-value (:policy-type a)
                                    :ref #(swap! policy-form assoc-in [(:id a) :policy-type] %)
                                    :label? true
                                    :max-width "100%"
                                    :required true}
                       [:option {:disabled true
                                 :value ""} "Type of Insurance"]
                       [:option {:value "Term"} "Term Life Insurance"]
                       [:option {:value "LTC"} "Long Term Care"]
                       [:option {:value "Disability"} "Disability"]
                       [:option {:value "Permanent Life Insurance"} "Permanent Life Insurance"]
                       [:option {:value "Employer Policy"} "Employer Policy"]
                       [:option {:value "Annuity"} "Annuity"]
                       [:option {:value "Other"} "Other"]]]
                     [form/input-row-s {:grid-column "span 3"}
                      [form/input-label-s "Carrier"]
                      [form/select {:default-value (:carrier a)
                                    :ref #(swap! policy-form assoc-in [(:id a) :carrier] %)
                                    :label? true
                                    :max-width "100%"
                                    :required true}
                       [:option {:disabled true
                                 :value ""} "Carrier"]
                       [:option {:value "Accordia Life"} "Accordia Life"]
                       [:option {:value "Banner Life"} "Banner Life"]
                       [:option {:value "Chubb"} "Chubb"]
                       [:option {:value "Genworth"} "Genworth"]
                       [:option {:value "John Hancock"} "John Handcock"]
                       [:option {:value "Lincoln Benefit Life"} "Lincoln Benefit Life"]
                       [:option {:value "Lincoln Financial"} "Lincoln Financial"]
                       [:option {:value "Mass Mutual"} "Mass Mutual"]
                       [:option {:value "Metlife"} "Metlife"]
                       [:option {:value "Mutual of Omaha"} "Mutual of Omaha"]
                       [:option {:value "One America"} "One America"]
                       [:option {:value "Penn Mutual"} "Penn Mutual"]
                       [:option {:value "Principal"} "Principal"]
                       [:option {:value "Protective"} "Protective"]
                       [:option {:value "Prudential"} "Prudential"]
                       [:option {:value "Standard"} "Standard"]
                       [:option {:value "Sunlife"} "Sunlife"]
                       [:option {:value "Symetra"} "Symetra"]
                       [:option {:value "Other"} "Other"]]]
                     [form/input-row-s {:grid-column "span 2"
                                        :currency? true}
                      [form/input-label-s "Policy amount"]
                      [form/input {:type "number"
                                   :label? true
                                   :unit? true
                                   :max-width "100%"
                                   :step 0.01
                                   :min 0
                                   :ref #(swap! policy-form assoc-in [(:id a) :amount] %)
                                   :default-value (:amount a)
                                   :required true}]]
                     [form/input-row-s {:grid-column "span 2"
                                        :currency? true}
                      [form/input-label-s "Premium amount"]
                      [form/input {:type "number"
                                   :label? true
                                   :unit? true
                                   :max-width "100%"
                                   :step 0.01
                                   :min 0
                                   :ref #(swap! policy-form assoc-in [(:id a) :premium-amount] %)
                                   :default-value (:premium-amount a)
                                   :required true}]]
                     [form/input-row-s {:grid-column "span 2"}
                      [form/input-label-s "Premium due date"]
                      [form/future-date {:ref #(swap! policy-form assoc-in [(:id a) :premium-due-date] %)
                                         :label? true
                                         :max-width "100%"
                                         :default-value (to-local-time (:premium-due-date a) "YYYY-MM-DD")
                                         :required true}]]
                     [form/input-row-s {:grid-column "span 3"}
                      [form/input-label-s "Date policy was issued"]
                      [form/past-date {:ref #(swap! policy-form assoc-in [(:id a) :issue-date] %)
                                       :label? true
                                       :max-width "100%"
                                       :default-value (to-local-time (:issue-date a) "YYYY-MM-DD")
                                       :required true}]]
                     [form/input-row-s {:grid-column "span 3"}
                      [form/input-label-s "Expiration date"]
                      [form/future-date {:ref #(swap! policy-form assoc-in [(:id a) :exp-date] %)
                                         :label? true
                                         :max-width "100%"
                                         :default-value (to-local-time (:exp-date a) "YYYY-MM-DD")
                                         :required true}]]
                     [form/input-row-s {:grid-column "span 6"}
                      [form/input-label-s "Beneficiaries"]
                      [form/input {:type "text"
                                   :label? true
                                   :max-width "100%"
                                   :ref #(swap! policy-form assoc-in [(:id a) :beneficiaries] %)
                                   :default-value (:beneficiaries a)
                                   :required false}]]
                     [common/center-s {:grid-column "span 6"}
                      [form/naked-button-s {:on-click #(remove-policy a)
                                            :type "button"
                                            :font-size "16px"}
                       [form/naked-button-symbol-s "−"]
                       [form/naked-button-text-s "Remove policy"]]]]))]]
         [common/center-s
          [form/naked-button-s {:on-click add-policy
                                :type "button"
                                :font-size "16px"
                                :margin "2rem 0 0"}
           [form/naked-button-symbol-s "+"]
           [form/naked-button-text-s "Add another policy"]]]
         [form/button-row-s
          [form/secondary-button-s {:on-click on-cancel
                                    :type "button"} cancel-label]
          [form/button {:type "submit"} "Save"]]]]])))

(defn insurance-policy-row [{:keys [policy-type carrier amount]}]
  [insurance-policy-row-s
   [common/data-row-full {:label "Policy Type"
                          :value policy-type}]
   [common/data-row-full {:label "Carrier"
                          :value carrier}]
   [common/data-row-full {:label "Amount"
                          :value (fmt/currency-format amount)}]])

(defn insurance [{:keys [on-edit]}]
  (let [insurance-policies-total (rf/subscribe [:profile/insurance-policies-total])
        insurance-policies (rf/subscribe [:insurance])]
    [:section
     [common/profile-section-header {:link on-edit
                                     :text "Edit"
                                     :title "Insurance"}]
     (if (some not-empty @insurance-policies)
       [:div
        [:ul
         (for [p @insurance-policies]
           ^{:key (:id p)}
           [:li [insurance-policy-row p]])]
        [common/data-row-full {:label "Total"
                               :value (fmt/currency-format @insurance-policies-total)
                               :size "large"}]]
       [common/no-results
        {:text (long-str "When the unexpected happens, are you prepared? "
                         "Add your insurance information to help us evaluate if you have appropriate coverage should the unforeseen occur.")
         :button-text "Enter your insurance"
         :button-click on-edit}])]))
